import { type BlockConfig, iconField, linkField } from './_types';

/**
 * Payload `Block` config sidecar for the `heroCompact` block.
 *
 * Generalized hero used by the marketing pages that don't need the home
 * hero's phone-mockup composition (Webinars, Partners, ContactUs,
 * ContactSales, Careers, Blog, BreachNews, HelpCenter, DownloadApp,
 * ReklaimInvest, Optout, Signup). Investors keeps its custom hero
 * because its stock-ticker bar + Contact-IR modal trigger are page-shell
 * concerns, not block concerns.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `hero-compact.tsx` / `hero-compact.payload.ts`
 *   React component export:  `HeroCompact`
 *   Payload `slug`:          `heroCompact`
 *   Payload `interfaceName`: `HeroCompactBlock`
 */

export const HeroCompactBlock: BlockConfig = {
  slug: 'heroCompact',
  interfaceName: 'HeroCompactBlock',
  labels: {
    singular: 'Hero Compact',
    plural: 'Hero Compact Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow',
      admin: {
        description:
          'Optional small label above the headline. Renders as a `SectionBadge` by default; switch the variant to "pill" for the icon-and-text rounded chip used by Help Center and Breach News.',
      },
    },
    {
      ...iconField({
        name: 'eyebrowIcon',
        label: 'Eyebrow icon (lucide-react name)',
      }),
      admin: {
        description:
          'Optional lucide-react icon rendered inside the eyebrow pill. Allowed names: ArrowRight, BookOpen, Bell, ChevronDown, ChevronRight, DollarSign, ExternalLink, Mail, MessageCircle, Search, Shield, Sparkles, Star. Unknown names fall back to Sparkles.',
      },
    },
    {
      name: 'eyebrowVariant',
      type: 'select',
      defaultValue: 'badge',
      label: 'Eyebrow variant',
      options: [
        { label: 'Section badge', value: 'badge' },
        { label: 'Pill (icon + text)', value: 'pill' },
      ],
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'headlineEmphasis',
      type: 'text',
      label: 'Headline emphasis',
      admin: {
        description:
          'Optional emphasized phrase rendered in the primary color directly after the headline.',
      },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: {
        rows: 3,
      },
    },
    {
      name: 'backgroundImage',
      type: 'text',
      label: 'Background image URL',
      admin: {
        description:
          'Optional decorative image rendered behind the hero at 30% opacity. Use the imported asset URL.',
      },
    },
    {
      name: 'backgroundOverlay',
      type: 'select',
      defaultValue: 'none',
      label: 'Background overlay',
      options: [
        { label: 'None', value: 'none' },
        { label: 'Gradient (subtle)', value: 'gradient' },
        { label: 'Gradient (strong)', value: 'gradientStrong' },
      ],
      admin: {
        description:
          'Apply a foreground-fade gradient over the background image. Use "Gradient (strong)" when the image is busy.',
      },
    },
    {
      name: 'primaryCta',
      type: 'group',
      label: 'Primary CTA',
      admin: {
        description:
          'Optional. Leave both Button text + Button link blank to render the hero without a primary CTA. The block treats a CTA as "present" only when both fields are non-empty.',
      },
      fields: [
        // Button text + link are intentionally NOT marked required so a
        // CMS author can leave the entire CTA off without a per-row
        // boolean toggle. The React block guards via isCtaPresent().
        { name: 'text', type: 'text', label: 'Button text' },
        linkField({ name: 'link', label: 'Button link' }),
        {
          name: 'external',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
        },
        iconField({
          name: 'iconRight',
          label: 'Right icon (lucide-react name)',
        }),
      ],
    },
    {
      name: 'secondaryCta',
      type: 'group',
      label: 'Secondary CTA',
      admin: {
        description:
          'Optional. Leave both Button text + Button link blank to render the hero without a secondary CTA. The block treats a CTA as "present" only when both fields are non-empty.',
      },
      fields: [
        { name: 'text', type: 'text', label: 'Button text' },
        linkField({ name: 'link', label: 'Button link' }),
        {
          name: 'external',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
        },
        iconField({
          name: 'iconRight',
          label: 'Right icon (lucide-react name)',
        }),
        {
          name: 'variant',
          type: 'select',
          defaultValue: 'outline',
          label: 'Variant',
          options: [
            { label: 'Outline', value: 'outline' },
            { label: 'Default', value: 'default' },
            { label: 'Ghost', value: 'ghost' },
          ],
        },
      ],
    },
    {
      name: 'extraContent',
      type: 'array',
      label: 'Trust indicators',
      labels: { singular: 'Item', plural: 'Items' },
      maxRows: 6,
      admin: {
        description:
          'Optional list of small trust indicators rendered below the CTAs (e.g. "No credit card required", "90 second signup").',
      },
      fields: [
        iconField({
          name: 'icon',
          label: 'Icon (optional, lucide-react name)',
        }),
        { name: 'text', type: 'text', required: true, label: 'Text' },
      ],
    },
  ],
};

export default HeroCompactBlock;
